import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { queryView, useOpenWindow, useWindow, useWindowShell } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  RmkSourcesScreen,
  grantsOf,
  platformMyResources,
  type MyResource,
  type RmkSourceTileVm,
  type RmkSourcesActions,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { CONNECTION_FORMS } from '../../composition/connectionForms'
import { RMK_SOURCES } from '../../composition/rmkSources'
import type { RootState } from '../../app/types'
import { tileOf } from './resourceTiles'
import { DownSourceSignals } from './SourceHealthSignal'
import { navigationOrigin } from './unknownShapes'
import { useMyAccess } from './useMyAccess'
import type { RmkSourceExtension, RmkSourceSummary } from './rmkSourceTypes'

const RMK_ARGS = { capability: 'session.open' } as const
const selectSources = platformMyResources.select(RMK_ARGS)
/** Кабинет читается для сводки, пока плиток немного (UI-RMK-FLOW 3.4). */
const SUMMARY_WITH_CABINET_UP_TO = 3
const NO_SUMMARY: RmkSourceSummary = { ownWork: null, lines: [] }

/** Читает сводку одного источника хуком расширения вида и отдаёт её родителю: порядок хуков — по плитке. */
function SummaryProbe({
  resource,
  extension,
  withCabinet,
  onSummary,
}: {
  resource: MyResource
  extension: RmkSourceExtension
  withCabinet: boolean
  onSummary: (id: string, summary: RmkSourceSummary) => void
}) {
  const summary = extension.useSummary(resource.id, withCabinet)
  useEffect(() => onSummary(resource.id, summary), [onSummary, resource.id, summary])
  return null
}

/**
 * Вход раздела «РМК» (UI-RMK-FLOW 3.4): плитки источников, где у человека есть работа за кассой, — даже когда
 * источник один. «Открыть» ведёт на адрес окна РМК источника. Автопереход А1: своя работа ровно в одном
 * источнике и человек вошёл в раздел извне — плитки пропускаются; из окна РМК («Другой источник», «← назад»)
 * и после F5 оттуда автоперехода нет, иначе из продажи нельзя было бы вернуться к плиткам.
 */
export function RmkSourcesBinding() {
  const window = useWindow()
  const shell = useWindowShell()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const location = useLocation()
  platformMyResources.useQuerySubscription(RMK_ARGS, { refetchOnMountOrArgChange: true })
  const result = useAppSelector((state: RootState) => selectSources(state))
  const access = useMyAccess()
  const view = useMemo(() => queryView(result, () => false), [result])
  const resources = useMemo(
    () => CONNECTION_FORMS.sortSources((result.data ?? []).filter((resource) => RMK_SOURCES.has(resource.kind))),
    [result.data],
  )

  const [summaries, setSummaries] = useState<Readonly<Record<string, RmkSourceSummary>>>({})
  const onSummary = useMemo(
    () => (id: string, summary: RmkSourceSummary) =>
      setSummaries((current) => {
        const known = current[id]
        const same =
          known !== undefined && known.ownWork === summary.ownWork && known.lines.join('\n') === summary.lines.join('\n')
        return same ? current : { ...current, [id]: summary }
      }),
    [],
  )

  // А1: автопереход один раз за вход, и только пока все сводки прочитаны.
  const jumped = useRef(false)
  useEffect(() => {
    if (jumped.current || view.status !== 'ready' || resources.length === 0) return
    const { back, from } = navigationOrigin(location.state)
    const fromPath = from?.split('?')[0]
    const fromRmk = fromPath !== undefined && shell.match(fromPath)?.section === 'rmk'
    if (back || fromRmk) {
      jumped.current = true
      return
    }
    if (resources.some((resource) => summaries[resource.id]?.ownWork == null)) return
    jumped.current = true
    const withWork = resources.filter((resource) => summaries[resource.id]?.ownWork === true)
    const only = withWork.length === 1 ? withWork[0] : undefined
    const extension = only === undefined ? undefined : RMK_SOURCES.get(only.kind)
    if (only !== undefined && extension !== undefined) window.replaceWith(extension.window, { rid: only.id })
  }, [location.state, resources, shell, summaries, view.status, window])

  const tiles = useMemo<RmkSourceTileVm[]>(
    () =>
      resources.map((resource) => tileOf(resource, (summaries[resource.id] ?? NO_SUMMARY).lines)),
    [resources, summaries],
  )

  const actions = useMemo<RmkSourcesActions>(
    () => ({
      open: (id) => {
        const kind = resources.find((resource) => resource.id === id)?.kind
        const extension = kind === undefined ? undefined : RMK_SOURCES.get(kind)
        if (extension !== undefined) openWindow(extension.window, { rid: id })
      },
      reload: () => {
        void dispatch(platformMyResources.initiate(RMK_ARGS, { forceRefetch: true }))
      },
      openResources: () => openWindow(PLATFORM_WINDOWS.resources),
    }),
    [dispatch, openWindow, resources],
  )

  return (
    <>
      {view.status === 'ready' &&
        resources.map((resource) => {
          const extension = RMK_SOURCES.get(resource.kind)
          return extension === undefined ? null : (
            <SummaryProbe
              key={resource.id}
              resource={resource}
              extension={extension}
              withCabinet={resources.length <= SUMMARY_WITH_CABINET_UP_TO}
              onSummary={onSummary}
            />
          )
        })}
      <DownSourceSignals resources={resources} />
      <RmkSourcesScreen
        vm={{ query: view, tiles, canManageResources: grantsOf(access).registryRead }}
        actions={actions}
      />
    </>
  )
}
